import { RoiId } from '../changes/ids';

export interface RoiDisplayPreferences {
    visible: boolean;
    contourThickness: number;
}

/**
 * Frozen default preferences. Returned by reference (not as a fresh
 * literal) from {@link DisplayPreferencesStore.getPreferences} so the
 * snapshot value stays referentially stable across reads — required by
 * {@link React.useSyncExternalStore}, which re-renders whenever the
 * snapshot identity changes.
 */
const DEFAULT_PREFERENCES: RoiDisplayPreferences = Object.freeze({
    visible: true,
    contourThickness: 1,
});

export class DisplayPreferencesStore {
    private prefs = new Map<RoiId, RoiDisplayPreferences>();
    private listeners = new Set<() => void>();
    private version = 0;

    subscribe = (listener: () => void): (() => void) => {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    };

    getVersion = (): number => this.version;

    getPreferences(id: RoiId): RoiDisplayPreferences {
        return this.prefs.get(id) ?? DEFAULT_PREFERENCES;
    }

    setVisible(id: RoiId, visible: boolean): void {
        const cur = this.getPreferences(id);
        this.prefs.set(id, { ...cur, visible });
        this.notify();
    }

    setThickness(id: RoiId, thickness: number): void {
        const cur = this.getPreferences(id);
        this.prefs.set(id, { ...cur, contourThickness: thickness });
        this.notify();
    }

    private notify(): void {
        this.version++;
        this.listeners.forEach((l) => l());
    }
}
